import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';
import { SortingState } from '@tanstack/react-table';
import { useLocation, useRouter } from '@tanstack/react-router';
import { DateFilterData } from '../components/sub-topic-options/components/date-filter';
import {
  OrderListCompoundFilterState,
  OrderListFilterBranchOptions,
  OrderListFilterContactOptions,
  OrderListFilterDateOptions,
  OrderListFilterDeliveryOptions,
  OrderListFilterPickupOptions,
  OrderListFilterStatusOptions,
} from '../hooks/use-order-list-compound-filter';
import { CompoundFilterStateOption } from '@trukt/ui/CompoundFilter';
import {
  buildDataFromLocation,
  LocationRadiusFilterData,
} from '../components/sub-topic-options/components/location-filter';
import { BranchFilterData } from '../components/sub-topic-options/components/SearchBranchInput';
import { ContactFilterData } from '../components/sub-topic-options/components/SearchContactInput';
import { ORDER_STATUS_MAP } from '@trukt/portal-utils/OrderStatusMap';
import { ExternalCompoundFilterState } from '@trukt/ui/CompoundFilter';

export type OrderListSearchParamsState = {
  status: ORDER_STATUS_MAP[];
  contacts: ContactFilterData[];
  dates: DateFilterData[];
  pickup: LocationRadiusFilterData[];
  delivery: LocationRadiusFilterData[];
  branches: BranchFilterData[];
};

export type OrderListRouteSearch = Record<string, unknown> & {
  filters?: string;
  sort?: string;
  globalSearch?: string;
};

type OrderListProviderState = {
  setFilters(state: OrderListCompoundFilterState): void;
  getFilters(): OrderListSearchParamsState;
  setSort(sortingState: SortingState): void;
  getSort(): SortingState;
};

type UnknownExternalType = ExternalCompoundFilterState | OrderListSearchParamsState;

const OrderListSearchParamsContext = createContext<OrderListProviderState | null>(null);

export function useOrderListSearchParamsContext() {
  const context = useContext(OrderListSearchParamsContext);

  if (!context) {
    throw new Error(
      'useOrderListSearchParamsContext must be used within OrderListSearchParamsProvider',
    );
  }

  return context;
}

export enum SEARCH_PARAM_KEYS {
  FILTERS = 'filters',
  SORT = 'sort',
  GLOBAL_SEARCH = 'globalSearch',
}

interface OrderListSearchParamsProviderProps {
  children: ReactNode;
}

export function useOrderListRouteSearchParams() {
  const location = useLocation();
  const router = useRouter();
  const searchParams = useMemo(() => new URLSearchParams(location.searchStr), [location.searchStr]);
  const setSearchParams = useCallback(
    (next: URLSearchParams | ((previous: URLSearchParams) => URLSearchParams)) => {
      const previous = new URLSearchParams(location.searchStr);
      const resolved = typeof next === 'function' ? next(previous) : next;
      const search = resolved.toString();
      const href = `${location.pathname}${search ? `?${search}` : ''}${location.hash}`;

      return router.history.push(href);
    },
    [location.hash, location.pathname, location.searchStr, router.history],
  );

  return [searchParams, setSearchParams] as const;
}

export function OrderListSearchParamsProvider({ children }: OrderListSearchParamsProviderProps) {
  const [searchParams, setSearchParams] = useOrderListRouteSearchParams();

  const getFilters: OrderListProviderState['getFilters'] = useCallback(() => {
    const defaultFilters = getDefaultFilters();
    try {
      const queryParam = searchParams.get(SEARCH_PARAM_KEYS.FILTERS);
      const decodedQueryParams: UnknownExternalType = queryParam
        ? JSON.parse(decodeURIComponent(queryParam))
        : defaultFilters;

      return isOldQueryParams(decodedQueryParams)
        ? fromOldUrlStateToNewFilterStructure(decodedQueryParams)
        : decodedQueryParams;
    } catch (_e) {
      return defaultFilters;
    }
  }, [searchParams]);

  const setFilters: OrderListProviderState['setFilters'] = useCallback(
    (state) => {
      const filters = state.reduce<OrderListSearchParamsState>((acc, current) => {
        if (!acc) return acc;

        switch (current.id) {
          case 'status':
            acc.status = mapIds(current.options as OrderListFilterStatusOptions);
            break;

          case 'contacts':
            acc.contacts = mapData(current.options as OrderListFilterContactOptions);
            break;

          case 'dates':
            acc.dates = mapData(current.options as OrderListFilterDateOptions);
            break;

          case 'pickup':
            acc.pickup = mapData(current.options as OrderListFilterPickupOptions);
            break;

          case 'delivery':
            acc.delivery = mapData(current.options as OrderListFilterDeliveryOptions);
            break;

          case 'branches':
            acc.branches = mapData(current.options as OrderListFilterBranchOptions);
            break;

          default:
            break;
        }

        return acc;
      }, getDefaultFilters());

      const filtersJSON = JSON.stringify(filters);
      const encodedFilters = encodeURIComponent(filtersJSON);

      setSearchParams((oldValue) => {
        const newValue = new URLSearchParams(oldValue);
        newValue.set(SEARCH_PARAM_KEYS.FILTERS, encodedFilters);
        return newValue;
      });
    },
    [setSearchParams],
  );

  const setSort: OrderListProviderState['setSort'] = useCallback(
    (sortingState) => {
      setSearchParams((previousSearchParams) => {
        const nextSearchParams = new URLSearchParams(previousSearchParams);
        if (!sortingState.length) {
          nextSearchParams.delete(SEARCH_PARAM_KEYS.SORT);
          return nextSearchParams;
        }

        const sortingJSON = JSON.stringify(sortingState);
        const encodedSort = encodeURIComponent(sortingJSON);

        nextSearchParams.set(SEARCH_PARAM_KEYS.SORT, encodedSort);
        return nextSearchParams;
      });
    },
    [setSearchParams],
  );

  const getSort: OrderListProviderState['getSort'] = useCallback(() => {
    const defaultSorting = [{ id: 'dateUpdated', desc: true }];
    try {
      const queryParam = searchParams.get(SEARCH_PARAM_KEYS.SORT);
      const sorting = queryParam ? JSON.parse(decodeURIComponent(queryParam)) : defaultSorting;

      return Array.isArray(sorting) ? sorting : [sorting];
    } catch (_e) {
      return defaultSorting;
    }
  }, [searchParams]);

  const value = useMemo(
    () => ({ setFilters, getFilters, setSort, getSort }),
    [setFilters, getFilters, setSort, getSort],
  );

  return (
    <OrderListSearchParamsContext.Provider value={value}>
      {children}
    </OrderListSearchParamsContext.Provider>
  );
}

function getDefaultFilters(): OrderListSearchParamsState {
  return {
    status: [],
    contacts: [],
    dates: [],
    pickup: [],
    delivery: [],
    branches: [],
  };
}

function mapIds<D, Id extends string>(options: CompoundFilterStateOption<D, Id>[]) {
  return options.filter((option) => option.stateModifiers?.isActive).map((option) => option.id);
}

function mapData<D, Id extends string>(options: CompoundFilterStateOption<D, Id>[]) {
  return options
    .filter((option) => option.stateModifiers?.isActive && option.data !== undefined)
    .map((option) => option.data!);
}

// In case filters have the old structure, we need to convert them to the new one.
// Helping users to not lose their bookmarks.
export function fromOldUrlStateToNewFilterStructure(
  externalQueryParams: ExternalCompoundFilterState,
): OrderListSearchParamsState {
  const newStructure = externalQueryParams.itemIds.reduce<OrderListSearchParamsState>(
    (acc, id, index) => {
      const options = externalQueryParams.options[index];
      const selectedOptions = externalQueryParams.selectedOptionIndexs[index];
      switch (id) {
        case 'status':
          acc.status = selectedOptions.map((optionIndex) => options[optionIndex].value);
          break;
        case 'contact':
        case 'contacts':
          acc.contacts = selectedOptions.map((optionIndex) => ({
            label: options[optionIndex].label,
            value: options[optionIndex].value,
          }));
          break;
        case 'dates':
          acc.dates = selectedOptions.map((optionIndex) => ({
            dateTypeValue: options[optionIndex].value.status,
            dateStart: options[optionIndex].value.startDate,
            dateEnd: options[optionIndex].value.endDate,
            dateType: options[optionIndex].value.status,
            mode:
              options[optionIndex].value.endDate === options[optionIndex].value.startDate
                ? 'same-date'
                : 'range',
          }));
          break;
        case 'pickup':
          acc.pickup = selectedOptions.map((optionIndex) => {
            const option = options[optionIndex];
            const label = option?.value?.miles
              ? option.label.replace(`| ${option.value.miles} Mi`, '')
              : option.label;
            return buildDataFromLocation(
              { label, value: option.value, raw: option.value?.location },
              option?.value?.miles ?? '',
            );
          });
          break;
        case 'delivery':
          acc.delivery = selectedOptions.map((optionIndex) => {
            const option = options[optionIndex];
            const label = option?.value?.miles
              ? option.label.replace(`| ${option.value.miles} Mi`, '')
              : option.label;
            return buildDataFromLocation(
              { label, value: option.value, raw: option.value?.location },
              option?.value?.miles ?? '',
            );
          });
          break;
        case 'branch':
        case 'branches':
          acc.branches = selectedOptions.map((optionIndex) => {
            const option = options[optionIndex];
            return { id: option.value, label: option.label };
          });
          break;
        default:
          break;
      }
      return acc;
    },
    { status: [], contacts: [], dates: [], pickup: [], delivery: [], branches: [] },
  );

  return newStructure;
}

function isOldQueryParams(
  queryParams: UnknownExternalType,
): queryParams is ExternalCompoundFilterState {
  return (
    'itemIds' in queryParams &&
    'selectedItemIndex' in queryParams &&
    'selectedOptionIndexs' in queryParams &&
    'options' in queryParams
  );
}
